import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import copy from 'copy-to-clipboard';
import apiService from '../../services/api';

interface ExportPrivateKeyModalProps {
  onClose: () => void;
}

type ExportState = 'warning' | 'password' | 'success' | 'error';

const ExportPrivateKeyModal: React.FC<ExportPrivateKeyModalProps> = ({ onClose }) => {
  const [state, setState] = useState<ExportState>('warning');
  const [password, setPassword] = useState('');
  const [privateKey, setPrivateKey] = useState('');
  const [publicKey, setPublicKey] = useState('');
  const [exportsRemaining, setExportsRemaining] = useState<number>(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleContinue = () => {
    setState('password');
  };

  const handleExport = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!password.trim()) {
      setError('Password is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await apiService.wallet.exportPrivateKey(password);
      setPrivateKey(response.private_key);
      setPublicKey(response.public_key);
      setExportsRemaining(response.exports_remaining);
      setState('success');
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Export failed. Please try again.';
      setError(errorMessage);

      // If rate limit error, stay on password screen
      // Otherwise move to error state
      if (err.response?.status !== 429) {
        setState('error');
      }
    } finally {
      setLoading(false);
      setPassword(''); // Clear password from memory
    }
  };

  const handleCopy = () => {
    copy(privateKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = () => {
    // Clear sensitive data before closing
    setPrivateKey('');
    setPassword('');
    onClose();
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-gray-900 rounded-2xl p-6 max-w-2xl w-full border border-gray-700 max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold flex items-center">
              {state === 'warning' && (
                <>
                  <svg className="w-6 h-6 text-yellow-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  Security Warning
                </>
              )}
              {state === 'password' && 'Enter Password'}
              {state === 'success' && (
                <>
                  <svg className="w-6 h-6 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Private Key Exported
                </>
              )}
              {state === 'error' && 'Export Failed'}
            </h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Warning State */}
          {state === 'warning' && (
            <div className="space-y-4">
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                <h4 className="text-red-400 font-semibold mb-2 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  CRITICAL: Read Before Proceeding
                </h4>
                <ul className="text-red-300 space-y-2 text-sm">
                  <li className="flex items-start">
                    <span className="text-red-400 mr-2">•</span>
                    <span>Your private key gives <strong>COMPLETE CONTROL</strong> over your wallet and all funds</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-400 mr-2">•</span>
                    <span><strong>NEVER SHARE</strong> your private key with anyone, including support staff</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-400 mr-2">•</span>
                    <span>Anyone with your private key can <strong>STEAL ALL YOUR FUNDS</strong></span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-400 mr-2">•</span>
                    <span>Store it <strong>OFFLINE</strong> in a secure location (not on your computer or cloud)</span>
                  </li>
                </ul>
              </div>

              <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                <h4 className="text-yellow-400 font-semibold mb-2">Beware of Phishing</h4>
                <p className="text-yellow-300 text-sm">
                  Legitimate services will <strong>NEVER</strong> ask for your private key. If someone requests it,
                  they are trying to scam you. Only export your private key if you need to backup your wallet or
                  migrate to another wallet application.
                </p>
              </div>

              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                <h4 className="text-blue-400 font-semibold mb-2">Export Limit</h4>
                <p className="text-blue-300 text-sm">
                  For security reasons, you can only export your private key <strong>5 times per 24 hours</strong>.
                  This helps prevent unauthorized access attempts.
                </p>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={handleClose}
                  className="flex-1 px-6 py-3 bg-gray-700 hover:bg-gray-600 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleContinue}
                  className="flex-1 px-6 py-3 bg-red-600 hover:bg-red-500 rounded-lg font-medium transition-colors"
                >
                  I Understand, Continue
                </button>
              </div>
            </div>
          )}

          {/* Password State */}
          {state === 'password' && (
            <form onSubmit={handleExport} className="space-y-4">
              <p className="text-gray-300 text-sm">
                Enter your password to decrypt and export your wallet's private key.
              </p>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all"
                  placeholder="Enter your password"
                  disabled={loading}
                  autoFocus
                />
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-red-300 text-sm">
                  {error}
                </div>
              )}

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 px-6 py-3 bg-gray-700 hover:bg-gray-600 rounded-lg font-medium transition-colors"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-purple-600 hover:bg-purple-500 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  disabled={loading || !password.trim()}
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Exporting...
                    </>
                  ) : (
                    'Export Private Key'
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Success State */}
          {state === 'success' && (
            <div className="space-y-4">
              <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                <p className="text-green-300 text-sm mb-2">
                  Your private key has been exported successfully. You have <strong>{exportsRemaining} exports remaining</strong> in the next 24 hours.
                </p>
                <p className="text-green-300 text-sm">
                  An email notification has been sent to your registered email address for security purposes.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Public Key (Wallet Address)
                </label>
                <div className="px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg font-mono text-sm break-all">
                  {publicKey}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Private Key (Base58)
                </label>
                <div className="relative">
                  <div className="px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg font-mono text-sm break-all pr-12">
                    {privateKey}
                  </div>
                  <button
                    onClick={handleCopy}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 hover:bg-gray-700 rounded transition-colors"
                    title="Copy to clipboard"
                  >
                    {copied ? (
                      <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                <h4 className="text-red-400 font-semibold mb-2 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  Store Securely
                </h4>
                <ul className="text-red-300 space-y-1 text-sm">
                  <li>• Write it down on paper and store in a safe place</li>
                  <li>• Do NOT save it in a text file, email, or cloud storage</li>
                  <li>• Do NOT take a screenshot (malware can access your clipboard/screenshots)</li>
                  <li>• Consider using a hardware wallet for maximum security</li>
                </ul>
              </div>

              <button
                onClick={handleClose}
                className="w-full px-6 py-3 bg-purple-600 hover:bg-purple-500 rounded-lg font-medium transition-colors"
              >
                Close (Private Key Will Be Cleared)
              </button>
            </div>
          )}

          {/* Error State */}
          {state === 'error' && (
            <div className="space-y-4">
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-center">
                <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <h4 className="text-xl font-bold text-red-400 mb-2">Export Failed</h4>
                <p className="text-red-300 text-sm mb-4">
                  {error || 'An unexpected error occurred. Please try again.'}
                </p>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={handleClose}
                  className="flex-1 px-6 py-3 bg-gray-700 hover:bg-gray-600 rounded-lg font-medium transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => setState('password')}
                  className="flex-1 px-6 py-3 bg-purple-600 hover:bg-purple-500 rounded-lg font-medium transition-colors"
                >
                  Try Again
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ExportPrivateKeyModal;